// React error boundary for the kiosk. If anything below throws during
// render, the boundary catches it instead of letting React unmount the
// whole tree (which manifests as a fully blank page on the TV — the
// "had to reload" symptom). We log to Sentry, render a minimal fallback,
// and offer a self-reload after a short delay so the screen recovers
// without operator intervention.
//
// Limits: error boundaries do NOT catch errors in async callbacks,
// event handlers, or setTimeout chains. Those errors should still bubble
// through window.onerror → Sentry. The boundary's job is render errors.

import * as Sentry from '@sentry/react'
import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

const RECOVERY_DELAY_MS = 10_000

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }
  private recoveryTimer: number | null = null

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    try {
      Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
    } catch { /* sentry not configured — fall through */ }
    // Auto-reload after 10s so a glitched render doesn't trap the kiosk
    // until someone walks over with a remote.
    if (this.recoveryTimer === null) {
      this.recoveryTimer = window.setTimeout(() => {
        try { window.location.reload() } catch { /* ignore */ }
      }, RECOVERY_DELAY_MS)
    }
  }

  componentWillUnmount() {
    if (this.recoveryTimer !== null) {
      window.clearTimeout(this.recoveryTimer)
      this.recoveryTimer = null
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="absolute inset-0 bg-bg flex items-center justify-center text-white">
          <div className="flex flex-col items-center gap-3 px-12 text-center max-w-md">
            <span className="text-5xl font-black leading-none">:(</span>
            <h2 className="text-[22px] font-semibold">Something went wrong</h2>
            <p className="text-muted text-sm leading-relaxed">
              The screen will recover automatically in a few seconds.
            </p>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
